import { apiSlice } from '@/app/api/apiSlice';
import { Paged } from 'app/shared-components/Types/Paged';
import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'material-react-table';
import { createMyUtiliesURL } from '@/utils/helpers';
import { AdditionalFilter } from '@/app/hooks/useFilterPagination';

export enum MessagingRuleTrigger {
  Creation = 1,
  LeadsToSales = 2,
  SalesToProcessing = 3,
  ProcessingToCompleted = 4,
  Cancellation = 5,
  ManualStatusUpdate = 6,
  VaultLinkText = 7,
  VaultLinkEmail = 8,
  ForgotPassword = 9,
  TicketAssigned = 10,
  TicketClosed = 11,
  InsuranceInfo = 12,
  ErrorInLeadsViaEmail = 13,
  TermsAndConditions = 14,
  AICallScheduling = 15,
}

export enum DwellingType {
  House = 1,
  Apartment = 2,
}

export enum ChannelType {
  Sms = 1,
  Email = 2,
}

export interface EnumValue {
  value: number;
  label: string;
}

export interface MessagingRule {
  id: number;
  trigger: MessagingRuleTrigger;
  triggerName?: string | null;
  priority: number | null;
  channelTypeId: ChannelType | null;
  channelTypeName?: string | null;
  licenseeId: number | null;
  licenseeName?: string | null;
  referralCompanyId: number | null;
  referralCompanyName?: string | null;
  dwellingType: DwellingType | null;
  dwellingTypeName?: string | null;
  cancellationReasonId: number | null;
  cancellationReasonName?: string | null;
  useInsideBusinessHours: boolean | null;
  messageTemplateId: number | null;
  messageTemplateName?: string | null;
  messageTemplateContent?: string | null;
  emailTemplateId?: string | null;
  alternativeMessageTemplateId: number | null;
  alternativeMessageTemplateName?: string | null;
  alternativeMessageTemplateContent?: string | null;
  alternativeEmailTemplateId?: string | null;
  useAlternativeTemplates: boolean;
  alternativeConditionExpression: string | null;
  businessHoursStartHour: number | null;
  businessHoursStartMinute: number | null;
  businessHoursEndHour: number | null;
  businessHoursEndMinute: number | null;
  dontSend: boolean;
  lastUpdated?: Date;
  createdAt?: Date;
}

export interface CreateOrUpdateMessagingRule {
  trigger: MessagingRuleTrigger;
  licenseeIds?: number[];
  licenseeId?: number;
  referralCompanyIds?: number[];
  referralCompanyId?: number | null;
  dwellingTypes?: number[];
  dwellingType?: number | null | undefined;
  cancellationReasonIds?: number[];
  cancellationReasonId?: number | null | undefined;
  useInsideBusinessHours?: boolean | null;
  messageTemplateId?: number | null;
  emailTemplateId?: string | null;
  alternativeMessageTemplateId?: number | null;
  alternativeEmailTemplateId?: string | null;
  useAlternativeTemplates: boolean;
  alternativeConditionExpression?: string | null;
  businessHoursStartHour?: number | null;
  businessHoursStartMinute?: number | null;
  businessHoursEndHour?: number | null;
  businessHoursEndMinute?: number | null;
  dontSend?: boolean;
  // Channel type (1 = SMS, 2 = Email)
  channelType?: number;
}

export const messagingRulesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMessagingRules: builder.query<
      Paged<MessagingRule>,
      {
        filters: MRT_ColumnFiltersState;
        sorting: MRT_SortingState;
        pagination: MRT_PaginationState;
        extraFilters: AdditionalFilter[];
      }
    >({
      query: ({ filters, sorting, pagination, extraFilters }) => {
        const urlParams = createMyUtiliesURL(filters, sorting);
        const extraFiltersParams = new URLSearchParams();
        extraFilters.forEach((filter) => {
          extraFiltersParams.append(filter.name, filter.value as string);
        });

        return {
          url: apiRoutes.messagingRules,
          method: 'GET',
          params: {
            page: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            ...Object.fromEntries(urlParams),
            ...Object.fromEntries(extraFiltersParams),
          },
        };
      },
      providesTags: ['MessagingRules'],
    }),
    createMessagingRule: builder.mutation<MessagingRule, CreateOrUpdateMessagingRule>({
      query: (data) => ({
        url: apiRoutes.messagingRules,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['MessagingRules', 'MessageTemplates'],
    }),
    updateMessagingRule: builder.mutation<void, { id: number; data: CreateOrUpdateMessagingRule }>({
      query: ({ id, data }) => ({
        url: `${apiRoutes.messagingRules}/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['MessagingRules', 'MessageTemplates'],
    }),
    deleteMessagingRule: builder.mutation<void, number>({
      query: (id) => ({
        url: `${apiRoutes.messagingRules}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MessagingRules', 'MessageTemplates'],
    }),
    getTriggers: builder.query<EnumValue[], void>({
      query: () => ({
        url: `${apiRoutes.messagingRules}/triggers`,
        method: 'GET',
      }),
    }),
    getDwellingTypes: builder.query<EnumValue[], void>({
      query: () => ({
        url: `${apiRoutes.enums}/dwelling-types`,
        method: 'GET',
      }),
    }),
    getApplicableMessagingRulesByOrder: builder.query<MessagingRule[], number>({
      query: (orderId) => ({
        url: `${apiRoutes.messagingRules}/by-order/${orderId}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetMessagingRulesQuery,
  useCreateMessagingRuleMutation,
  useUpdateMessagingRuleMutation,
  useDeleteMessagingRuleMutation,
  useGetTriggersQuery,
  useGetDwellingTypesQuery,
  useGetApplicableMessagingRulesByOrderQuery,
} = messagingRulesApiSlice;
